import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, FileText, Headphones, TrendingUp, Mail, CheckCircle2, Clock, Shield, Route, BarChart3, Zap, Package, Calendar, Warehouse, Sparkles } from "lucide-react";
import BusinessLeadForm from "../components/BusinessLeadForm";
import B2BQuoteForm from "../components/B2BQuoteForm";
import RouteOptimizationDemo from "../components/RouteOptimizationDemo";
import useSeo from "../hooks/useSeo";

const B2B = "https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg";

// Service-levels presented WITHOUT public prices — customers must request a quote
const TIERS = [
  {
    tier: "Starter",
    deliveries: "Lav volumen — pay-as-you-go",
    note: "Test platformen risiko-frit",
    highlight: false,
    perks: [
      "Adgang til hele B2B-portalen",
      "Labels (10×15 + A6) + public tracking",
      "CSV/Excel import",
      "Pickup-manifests + PDF",
      "Månedligt faktura-grundlag",
    ],
  },
  {
    tier: "Growth",
    deliveries: "Voksende volumen",
    note: "Mest valgte",
    highlight: true,
    perks: [
      "Alt i Starter +",
      "API + webhooks (HMAC-signed)",
      "Sandbox til IT-test",
      "Failed-delivery alerts + Control Tower",
      "Margin overvågning + dedikeret kontaktperson",
    ],
  },
  {
    tier: "Scale",
    deliveries: "Stor volumen + SLA",
    note: "Større webshops + kæder",
    highlight: false,
    perks: [
      "Alt i Growth +",
      "SLA på leveringstid",
      "Faste afhentningsvinduer",
      "Vognmand-allokering pr. rute",
      "ESG/CO₂e-rapportering (kommer)",
    ],
  },
];

const USECASES = [
  { i: Building2, t: "Webshops", d: "Same-day for kunder i Sjælland → øget konvertering med 'Bestil før 18 — leveret i aften'." },
  { i: FileText, t: "Advokat & revisor", d: "Bekræftede dokument-leveringer med foto-proof og tidsstempel — fuldt sporbart." },
  { i: Headphones, t: "Restauranter & catering", d: "Aften-leverancer af mad, drikke og udstyr — på tværs af Sjælland." },
  { i: Shield, t: "Klinikker & apotek", d: "Diskrete leveringer af recepter, prøver og medicinsk udstyr." },
];

export default function BusinessPage() {
  useSeo({
    title: "Erhverv — B2B leveringsplatform · EMA Kurer ApS",
    description:
      "EMA Kurer er erhvervskurér og B2B-leveringsplatform for webshops, butikker og virksomheder på Sjælland. Portal, CSV-import, labels, API, tracking, månedsfaktura. Volumenbaseret prisaftale.",
    path: "/erhverv",
  });
  const [leadOpen, setLeadOpen] = React.useState(false);
  const [leadTier, setLeadTier] = React.useState("");
  const [quoteOpen, setQuoteOpen] = React.useState(false);
  const openLead = (tier = "") => { setLeadTier(tier); setLeadOpen(true); };
  const openQuote = () => setQuoteOpen(true);

  return (
    <div data-testid="business-page">
      <BusinessLeadForm open={leadOpen} onClose={() => setLeadOpen(false)} initialTier={leadTier} />
      <B2BQuoteForm open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-dark text-white">
        <div className="absolute inset-0 opacity-30">
          <img src={B2B} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="kicker text-brand-red">Erhvervskurér + B2B platform</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Erhvervslevering<br/>på Sjælland — og en hel platform med.
          </h1>
          <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
            EMA Kurer er både kurérvirksomhed og B2B-leveringsplatform for webshops, butikker og virksomheder på Sjælland. Same Day + Next Day-levering, B2B-portal, CSV/Excel-import, labels med QR, public tracking, pickup-manifests, failed-delivery flow og månedligt faktura-grundlag — alt samlet ét sted.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/erhverv/tilmeld" data-testid="business-cta-signup" className="btn-primary">
              Bliv erhvervskunde <ArrowRight size={16} />
            </Link>
            <Link to="/erhverv/faa-tilbud" data-testid="business-cta-quote" className="btn-ghost-light">Få tilbud</Link>
            <button type="button" onClick={openQuote} data-testid="business-cta-ai-quote" className="btn-ghost-light">
              <Sparkles size={16} /> Få AI-skræddersyet tilbud
            </button>
          </div>
          <p className="mt-3 text-sm text-white/60">Erhvervslevering på Sjælland — kontakt os for en prisaftale baseret på jeres volumen.</p>
        </div>
      </section>

      {/* B2B PLATFORM FEATURES STRIP */}
      <section className="bg-brand-bg2 border-y border-brand" data-testid="b2b-platform-features">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20">
          <div className="kicker">B2B-platformen</div>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-3 text-brand-dark">
            Hele værdikæden i ét system.
          </h2>
          <p className="text-brand-text2 mt-4 max-w-2xl">
            Når du bliver erhvervskunde, får du adgang til en komplet B2B-leveringsplatform — bygget specifikt til lokal Sjællands-logistik.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-8">
            {[
              { i: Package, t: "Same Day + Next Day" },
              { i: Calendar, t: "Aftenlevering 18-22" },
              { i: Building2, t: "B2B-portal" },
              { i: FileText, t: "CSV/Excel import" },
              { i: BarChart3, t: "Labels (10×15 + A6) + QR" },
              { i: Route, t: "Public tracking" },
              { i: Warehouse, t: "Pickup-manifests" },
              { i: Shield, t: "Failed-delivery flow" },
              { i: TrendingUp, t: "Redelivery + returer" },
              { i: Clock, t: "Månedligt faktura-grundlag" },
              { i: CheckCircle2, t: "Billy faktura-integration" },
              { i: Zap, t: "API + webhooks (HMAC)" },
              { i: Sparkles, t: "Sandbox til IT-test" },
              { i: BarChart3, t: "Control Tower + alerts" },
              { i: Headphones, t: "ESG/CO₂e (kommer)" },
            ].map((f) => (
              <div key={f.t} className="bg-white border border-brand p-4 flex items-start gap-3" data-testid={`b2b-platform-feature-${f.t.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`}>
                <f.i size={16} className="text-brand-red flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                <span className="text-sm text-brand-dark leading-tight">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE WAYS TO COLLABORATE — single, recurring, consolidation */}
      <section className="bg-white border-y border-slate-200" data-testid="b2b-ways">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
          <div className="max-w-3xl">
            <div className="kicker text-brand-red">Tre måder at samarbejde på</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4 text-brand-dark">
              Hvilken løsning passer din virksomhed?
            </h2>
            <p className="text-brand-text2 mt-5 leading-relaxed text-lg">
              Fra enkelt-leveringer til daglige ruter med konsolidering — vi tilpasser os jeres flow, ikke omvendt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {/* 1. Ad-hoc — Hurtig enkeltlevering (IKKE en erhvervsaftale) */}
            <div className="card-flat p-7 flex flex-col" data-testid="b2b-way-adhoc">
              <div className="w-12 h-12 bg-brand-dark text-white flex items-center justify-center"><Package size={20} /></div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-brand-red mt-5">Engangsbooking</div>
              <div className="font-display font-bold text-2xl text-brand-dark mt-2">Hurtig enkeltlevering</div>
              <div className="text-brand-text2 text-sm mt-3 leading-relaxed">
                Brug for at sende én pakke i dag uden en aftale? Book online på 60 sekunder og betal pr. levering — uden månedsfaktura eller binding.
              </div>
              <div className="mt-4 p-3 bg-amber-50 border-l-2 border-amber-400 text-xs text-amber-900 leading-relaxed">
                <strong>Bemærk:</strong> Dette er en engangsbooking — ikke en erhvervsaftale. Erhvervskunder med fast pakkevolumen bør i stedet bruge <strong>&laquo;Få tilbud&raquo;</strong> for en volumenbaseret prisaftale med månedsfaktura.
              </div>
              <ul className="mt-5 space-y-2 text-sm text-brand-text2">
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Pris beregnes i bookingflowet ud fra afstand og tid</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Direkte A → B uden omlastning</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Betal med kort eller MobilePay</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Ingen aftale eller binding</li>
              </ul>
              <div className="mt-auto pt-6">
                <Link to="/book?type=business" className="btn-primary w-full justify-center" data-testid="b2b-way-adhoc-cta">
                  Book engangslevering <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* 2. Faste afhentninger */}
            <div className="card-flat p-7 flex flex-col border-2 border-brand-red relative" data-testid="b2b-way-recurring">
              <div className="absolute -top-3 left-7 bg-brand-red text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1">Mest valgte</div>
              <div className="w-12 h-12 bg-brand-red text-white flex items-center justify-center"><Calendar size={20} /></div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-brand-red mt-5">Fast aftale</div>
              <div className="font-display font-bold text-2xl text-brand-dark mt-2">Faste daglige afhentninger</div>
              <div className="text-brand-text2 text-sm mt-3 leading-relaxed">
                Vi henter hver dag på samme tid hos jer, og kører pakkerne direkte til modtagerne. Månedsfaktura med volumenbaseret pris.
              </div>
              <ul className="mt-5 space-y-2 text-sm text-brand-text2">
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Volumenbaseret prisaftale — kontakt os</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Aftalt afhentningsvindue hver dag</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Direkte A → B for hver pakke</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Månedsfaktura · netto 14 dage</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Dedikeret kontaktperson</li>
              </ul>
              <div className="mt-auto pt-6">
                <a href="#pakker" className="btn-primary w-full justify-center" data-testid="b2b-way-recurring-cta">
                  Se pakker <ArrowRight size={16} />
                </a>
              </div>
            </div>

            {/* 3. Konsolidering */}
            <div className="card-flat p-7 flex flex-col" data-testid="b2b-way-consolidation">
              <div className="w-12 h-12 bg-brand-dark text-white flex items-center justify-center"><Warehouse size={20} /></div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-brand-red mt-5">Volume</div>
              <div className="font-display font-bold text-2xl text-brand-dark mt-2">Konsolidering & lager</div>
              <div className="text-brand-text2 text-sm mt-3 leading-relaxed">
                For jer med flere pakker på engang. Vi konsoliderer, rute-optimerer og henter fra eget lager efter aftale — én chauffør, mange stop.
              </div>
              <ul className="mt-5 space-y-2 text-sm text-brand-text2">
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Skræddersyet tilbud baseret på volume</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Rute-optimeret multi-stop levering</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> Mulighed for fast afhentning fra lager</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> API + CSV bulk-upload</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 shrink-0" /> SLA på leveringstid (≥95% on-time)</li>
              </ul>
              <div className="mt-auto pt-6">
                <button type="button" onClick={openQuote} className="btn-ghost-dark w-full justify-center" data-testid="b2b-way-consolidation-cta">
                  <Sparkles size={16} /> Få AI-skræddersyet tilbud
                </button>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-brand-text2 mt-10 max-w-2xl mx-auto">
            Usikker på hvilken løsning der passer? <button type="button" onClick={() => openLead("")} className="text-brand-red font-semibold underline hover:no-underline">Tag fat i os</button> — vi bygger en aftale der matcher jeres reelle behov.
          </p>
        </div>
      </section>

      <RouteOptimizationDemo />



      {/* USP STRIP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="b2b-usp">
        {[
          { i: Building2, t: "Dedikeret kontaktperson", d: "Én fast kontakt der kender din forretning." },
          { i: FileText, t: "Månedsfaktura (EAN/CVR)", d: "Slip for at betale pr. forsendelse — alt samles." },
          { i: Headphones, t: "Prioritet support", d: "Direkte linje til disponering i åbningstiden." },
          { i: TrendingUp, t: "Volumenrabat", d: "Bedre priser jo mere du sender." },
        ].map((b, i) => (
          <div key={b.t} className="card-flat p-7" data-testid={`b2b-usp-${i}`}>
            <div className="w-10 h-10 bg-brand-red text-white flex items-center justify-center mb-5"><b.i size={18} /></div>
            <div className="font-display font-semibold text-lg text-brand-dark">{b.t}</div>
            <div className="text-brand-text2 text-sm mt-2 leading-relaxed">{b.d}</div>
          </div>
        ))}
      </section>

      {/* PRICING TIERS */}
      <section id="pakker" className="bg-brand-bg2 border-y border-brand py-20 lg:py-28" data-testid="b2b-tiers">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-center">Erhvervsaftaler</div>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl mt-3 text-brand-dark tracking-tight text-center">Volumenbaseret prisaftale — skræddersyet til jer.</h2>
          <p className="text-brand-text2 mt-5 text-center max-w-2xl mx-auto leading-relaxed">EMA Kurer tilbyder afhentning, sortering og levering for virksomheder på Sjælland. Vi udregner jeres pris internt baseret på pakkevolumen, afhentningsadresse og leveringsbehov. Udfyld formularen, så kontakter vi jer.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {TIERS.map((t) => (
              <div
                key={t.tier}
                className={`${t.highlight ? "bg-brand-dark text-white border-brand-red ring-2 ring-brand-red" : "bg-white border-brand text-brand-dark"} border p-7 relative flex flex-col`}
                data-testid={`tier-${t.tier.toLowerCase()}`}
              >
                {t.highlight && <div className="absolute -top-3 left-6 bg-brand-red text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1">Anbefalet</div>}
                <div className={`text-[10px] tracking-[0.22em] uppercase ${t.highlight ? "text-white/70" : "text-brand-text2/70"}`}>{t.note}</div>
                <div className="font-display font-bold text-3xl mt-2">{t.tier}</div>
                <div className={`text-sm mt-3 font-semibold ${t.highlight ? "text-white" : "text-brand-dark"}`}>{t.deliveries}</div>

                <ul className="mt-6 space-y-2 text-sm flex-1">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <CheckCircle2 size={15} className={`${t.highlight ? "text-brand-red" : "text-brand-red"} mt-0.5 flex-shrink-0`} />
                      <span className={t.highlight ? "text-white/90" : "text-brand-text2"}>{p}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/erhverv/faa-tilbud"
                  className={`mt-7 ${t.highlight ? "bg-brand-red text-white hover:bg-white hover:text-brand-dark" : "bg-brand-dark text-white hover:bg-brand-red"} font-semibold tracking-wider uppercase text-xs px-5 py-3 inline-flex items-center justify-center gap-2 transition-colors`}
                  data-testid={`tier-cta-${t.tier.toLowerCase()}`}
                >
                  Få tilbud <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>

          <p className="text-brand-text2 text-sm mt-10 text-center">
            Større volumen eller særlige behov? <Link to="/erhverv/faa-tilbud" className="text-brand-red font-semibold underline" data-testid="tier-cta-custom">Bed om et skræddersyet tilbud</Link> — vi vender tilbage inden for én arbejdsdag.
          </p>
          <p className="text-brand-text2/80 text-xs mt-3 text-center max-w-3xl mx-auto leading-relaxed">
            EMA Kurer har i øjeblikket åbent for erhvervslevering på Sjælland. Vi arbejder løbende på at udvide til Fyn og Jylland.
          </p>
        </div>
      </section>

      {/* USE CASES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28" data-testid="b2b-usecases">
        <div className="kicker">Hvem bruger os</div>
        <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-3 text-brand-dark">Brancher der ikke har råd til at vente.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {USECASES.map((u, i) => (
            <div key={u.t} className="bg-white border border-brand p-7 hover:border-brand-dark transition-colors" data-testid={`usecase-${i}`}>
              <u.i className="text-brand-red" size={22} />
              <div className="font-display font-semibold text-xl text-brand-dark mt-5">{u.t}</div>
              <div className="text-brand-text2 text-sm mt-2 leading-relaxed">{u.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* OPERATIONAL TRUST */}
      <section className="bg-brand-dark text-white py-20 lg:py-28" data-testid="b2b-ops">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="kicker text-brand-red">Driften</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4">Erhvervskurér — direkte A → B.</h2>
            <p className="text-white/70 mt-5 leading-relaxed">
              Vi opererer som en moderne kurérvirksomhed, ikke et transportfirma. Single-pakker går direkte A→B uden mellemstop. Volume-aftaler kan inkludere konsolidering eller fast afhentning fra eget lager — alt med QR-baseret afhentning, foto-bevis, GPS-sporing og automatisk månedsfaktura.
            </p>
            <Link to="/kontakt" data-testid="ops-cta" className="btn-primary mt-8">Aftale et møde <ArrowRight size={16} /></Link>
            <button type="button" onClick={() => openLead("")} data-testid="ops-cta-form" className="btn-ghost-light mt-8 ml-2">Få et tilbud <Mail size={16} /></button>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            {[
              { i: Clock, t: "SLA på leveringstid", d: "Kontraktbestemt målsætning om ≥95% on-time levering inden aftalt cutoff." },
              { i: Route, t: "Rute-optimering", d: "Multi-pickup ruter for høj volumen." },
              { i: BarChart3, t: "Månedsrapport", d: "Volumen, leveringstid og hot-zones." },
              { i: Zap, t: "API-klar", d: "Integration med webshop, CRM og ERP." },
            ].map((f) => (
              <div key={f.t} className="bg-white/[0.04] border border-white/10 p-6">
                <f.i size={20} className="text-brand-red" />
                <div className="font-display font-semibold text-base mt-4">{f.t}</div>
                <div className="text-white/60 text-xs mt-2 leading-relaxed">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-bg2 border-y border-brand">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-20 text-center">
          <div className="kicker">Klar til samarbejde?</div>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl mt-4 text-brand-dark tracking-tight">
            Lad os bygge en logistik-løsning der passer til dig.
          </h2>
          <p className="text-brand-text2 mt-5 max-w-2xl mx-auto leading-relaxed">
            Send os en mail på <a href="mailto:kontakt@emakurer.dk" className="text-brand-red font-semibold underline">kontakt@emakurer.dk</a> — vi vender tilbage inden for én arbejdsdag med et tilbud skræddersyet til din virksomhed.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <button type="button" onClick={() => openLead("")} data-testid="final-cta-form" className="btn-primary">Få et tilbud <Mail size={16} /></button>
            <Link to="/book" data-testid="business-book-test" className="btn-secondary">Book en test-levering</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
